import { Suspense, lazy, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/components/providers/AuthProvider";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageLoader } from "@/components/common/PageLoader";
import type { BlogDocument } from "@/lib/blogs";
import { fetchBlogBySlug } from "@/lib/blogRepository";
import { usePathname } from "@/lib/router";
import { readPreviewBlog } from "@/lib/blogPreview";
import { persistLeadTrackingFromLocation } from "@/lib/leadTracking";
import { useDeferredMount } from "@/hooks/useDeferredMount";
import { SEO_METADATA } from "@/data/seoMetadata";
import { CW_IDENTITY_SCHEMA, loadPageSchema } from "@/data/pageSchema";
// Eager (in the entry bundle), framer-motion-free home hero. Rendered above the lazy
// ModernHomePageThree body so the hero — the mobile LCP element — paints from the entry
// without waiting for the page chunk or framer-motion. See src/pages/home/HomeHero.tsx.
import { HomeHero } from "@/pages/home/HomeHero";

const BlogArticlePage = lazy(() =>
  import("@/pages/BlogArticlePage").then((module) => ({ default: module.BlogArticlePage })),
);
const ModernHomePageThree = lazy(() =>
  import("@/pages/ModernHomePageThree").then((module) => ({ default: module.ModernHomePageThree })),
);
const BlogCreatePage = lazy(() =>
  import("@/pages/BlogCreatePage").then((module) => ({ default: module.BlogCreatePage })),
);
const BlogIndexPage = lazy(() =>
  import("@/pages/BlogIndexPage").then((module) => ({ default: module.BlogIndexPage })),
);
const BlogManagePage = lazy(() =>
  import("@/pages/BlogManagePage").then((module) => ({ default: module.BlogManagePage })),
);
const PortfolioManagementServicesTwoPage = lazy(() =>
  import("@/pages/product/PortfolioManagementServicesTwoPage").then((module) => ({
    default: module.PortfolioManagementServicesTwoPage,
  })),
);
const CorporateFixedDepositsTwoPage = lazy(() =>
  import("@/pages/product/CorporateFixedDepositsTwoPage").then((module) => ({
    default: module.CorporateFixedDepositsTwoPage,
  })),
);
const FixedIncomeProductPage = lazy(() =>
  import("@/pages/product/FixedIncomeProductPage").then((module) => ({
    default: module.FixedIncomeProductPage,
  })),
);
const MarketLinkedDebenturesPage = lazy(() =>
  import("@/pages/product/MarketLinkedDebenturesPage").then((module) => ({
    default: module.MarketLinkedDebenturesPage,
  })),
);
const MutualFundPage = lazy(() =>
  import("@/pages/product/MutualFundPage").then((module) => ({
    default: module.MutualFundPage,
  })),
);
const AlternativeInvestmentFundsPage = lazy(() =>
  import("@/pages/product/AlternativeInvestmentFundsPage").then((module) => ({
    default: module.AlternativeInvestmentFundsPage,
  })),
);
const SpecialisedInvestmentFundsPage = lazy(() =>
  import("@/pages/product/SpecialisedInvestmentFundsPage").then((module) => ({
    default: module.SpecialisedInvestmentFundsPage,
  })),
);
const NonConvertibleDebenturesPage = lazy(() =>
  import("@/pages/product/NonConvertibleDebenturesPage").then((module) => ({
    default: module.NonConvertibleDebenturesPage,
  })),
);
const BondsInvestmentServicesPage = lazy(() =>
  import("@/pages/product/BondsInvestmentServicesPage").then((module) => ({
    default: module.BondsInvestmentServicesPage,
  })),
);
const NationalPensionSystemPage = lazy(() =>
  import("@/pages/product/NationalPensionSystemPage").then((module) => ({
    default: module.NationalPensionSystemPage,
  })),
);
const NotFoundPage = lazy(() =>
  import("@/pages/NotFoundPage").then((module) => ({ default: module.NotFoundPage })),
);
const AdminLoginPage = lazy(() =>
  import("@/pages/AdminLoginPage").then((module) => ({ default: module.AdminLoginPage })),
);
const SetupCallPage = lazy(() =>
  import("@/pages/SetupCallPage").then((module) => ({ default: module.SetupCallPage })),
);
const SetupCallMinimalPage = lazy(() =>
  import("@/pages/SetupCallMinimalPage").then((module) => ({ default: module.SetupCallMinimalPage })),
);
const ServicesHubPage = lazy(() =>
  import("@/pages/ServicesHubPage").then((module) => ({ default: module.ServicesHubPage })),
);
const ProductsHubPage = lazy(() =>
  import("@/pages/product/ProductsHubTwoPage").then((module) => ({ default: module.ProductsHubTwoPage })),
);
const PrivacyPolicyPage = lazy(() =>
  import("@/pages/PrivacyPolicyPage").then((module) => ({ default: module.PrivacyPolicyPage })),
);
const TermsAndConditionsPage = lazy(() =>
  import("@/pages/TermsAndConditionsPage").then((module) => ({ default: module.TermsAndConditionsPage })),
);
const ChoiceGroupPage = lazy(() =>
  import("@/pages/ChoiceGroupPage").then((module) => ({ default: module.ChoiceGroupPage })),
);
const AboutUsPage = lazy(() =>
  import("@/pages/AboutUsPage").then((module) => ({ default: module.AboutUsPage })),
);
const OurPhilosophyPage = lazy(() =>
  import("@/pages/OurPhilosophyPage").then((module) => ({ default: module.OurPhilosophyPage })),
);
const CoreTeamPage = lazy(() =>
  import("@/pages/CoreTeamPage").then((module) => ({ default: module.CoreTeamPage })),
);
const BoardPage = lazy(() =>
  import("@/pages/BoardPage").then((module) => ({ default: module.BoardPage })),
);
const WealthManagersPage = lazy(() =>
  import("@/pages/WealthManagersPage").then((module) => ({ default: module.WealthManagersPage })),
);
const ContactUsPage = lazy(() =>
  import("@/pages/ContactUsPage").then((module) => ({ default: module.ContactUsPage })),
);
const HniPrivateWealthManagementPage = lazy(() =>
  import("@/pages/who-we-serve/HniPrivateWealthManagementPage").then((module) => ({
    default: module.HniPrivateWealthManagementPage,
  })),
);
const NriGlobalIndianWealthManagementPage = lazy(() =>
  import("@/pages/who-we-serve/NriGlobalIndianWealthManagementPage").then((module) => ({
    default: module.NriGlobalIndianWealthManagementPage,
  })),
);
const CorporateTreasurySolutionsPage = lazy(() =>
  import("@/pages/who-we-serve/CorporateTreasurySolutionsPage").then((module) => ({
    default: module.CorporateTreasurySolutionsPage,
  })),
);
const FamilyOfficeBusinessFamiliesWhoWeServePage = lazy(() =>
  import("@/pages/who-we-serve/FamilyOfficeBusinessFamiliesPage").then((module) => ({
    default: module.FamilyOfficeBusinessFamiliesPage,
  })),
);
const BusinessOwnersPromotersWhoWeServePage = lazy(() =>
  import("@/pages/who-we-serve/BusinessOwnersPromotersPage").then((module) => ({
    default: module.BusinessOwnersPromotersPage,
  })),
);
const FoundersEntrepreneursWhoWeServePage = lazy(() =>
  import("@/pages/who-we-serve/FoundersEntrepreneursPage").then((module) => ({
    default: module.FoundersEntrepreneursPage,
  })),
);
const CxosSeniorProfessionalsWhoWeServePage = lazy(() =>
  import("@/pages/who-we-serve/CxosSeniorProfessionalsPage").then((module) => ({
    default: module.CxosSeniorProfessionalsPage,
  })),
);
const PrivateClientWealthWhoWeServePage = lazy(() =>
  import("@/pages/who-we-serve/PrivateClientWealthPage").then((module) => ({
    default: module.PrivateClientWealthPage,
  })),
);
const WhoWeServeHubPage = lazy(() =>
  import("@/pages/who-we-serve/WhoWeServeHubPage").then((module) => ({
    default: module.WhoWeServeHubPage,
  })),
);
const PortfolioHealthReviewPage = lazy(() =>
  import("@/pages/services/PortfolioHealthReviewPage").then((module) => ({
    default: module.PortfolioHealthReviewPage,
  })),
);
const RetirementWealthReviewPage = lazy(() =>
  import("@/pages/services/RetirementWealthReviewPage").then((module) => ({
    default: module.RetirementWealthReviewPage,
  })),
);
const StructuredWealthReviewPage = lazy(() =>
  import("@/pages/services/StructuredWealthReviewPage").then((module) => ({
    default: module.StructuredWealthReviewPage,
  })),
);
const GlobalInvestmentSolutionsPage = lazy(() =>
  import("@/pages/services/GlobalInvestmentSolutionsPage").then((module) => ({
    default: module.GlobalInvestmentSolutionsPage,
  })),
);
const TaxAwarePortfolioReviewPage = lazy(() =>
  import("@/pages/services/TaxAwarePortfolioReviewPage").then((module) => ({
    default: module.TaxAwarePortfolioReviewPage,
  })),
);
const LegacyWealthTransferCoordinationPage = lazy(() =>
  import("@/pages/services/LegacyWealthTransferCoordinationPage").then((module) => ({
    default: module.LegacyWealthTransferCoordinationPage,
  })),
);

const V2PageLazy = lazy(() =>
  import("@/v2/V2Page").then((module) => ({ default: module.V2Page })),
);
const V2IndexPageLazy = lazy(() =>
  import("@/v2/V2IndexPage").then((module) => ({ default: module.V2IndexPage })),
);

type RouteMatch =
  | { type: "modernHomeThree" }
  | { type: "blogIndex" }
  | { type: "blogManage" }
  | { type: "adminLogin" }
  | { type: "blogCreate" }
  | { type: "blogPreview" }
  | { type: "setupCall" }
  | { type: "setupCallMinimal" }
  | { type: "productsHub" }
  | { type: "privacyPolicy" }
  | { type: "termsAndConditions" }
  | { type: "boardMembersPageTwo" }
  | { type: "aboutUs" }
  | { type: "ourPhilosophy" }
  | { type: "coreTeam" }
  | { type: "board" }
  | { type: "wealthManagers" }
  | { type: "contactUs" }
  | { type: "servicesHub" }
  | { type: "portfolioHealthReview" }
  | { type: "retirementWealthReview" }
  | { type: "structuredWealthReview" }
  | { type: "globalInvestmentSolutions" }
  | { type: "taxAwarePortfolioReview" }
  | { type: "legacyWealthTransferCoordination" }
  | { type: "whoWeServe" }
  | { type: "whoWeServeHniPrivateWealth" }
  | { type: "whoWeServeNriGlobalIndianWealth" }
  | { type: "whoWeServeCorporateTreasury" }
  | { type: "whoWeServeFamilyOffice" }
  | { type: "whoWeServeBusinessOwnersPromoters" }
  | { type: "whoWeServeFoundersEntrepreneurs" }
  | { type: "whoWeServeCxosSeniorProfessionals" }
  | { type: "whoWeServePrivateClientWealth" }
  | { type: "portfolioManagementServicesTwo" }
  | { type: "corporateFdTwo" }
  | { type: "fixedIncomeProduct" }
  | { type: "mldProduct" }
  | { type: "mutualFundProduct" }
  | { type: "aifProduct" }
  | { type: "sifProduct" }
  | { type: "ncdProduct" }
  | { type: "npsProduct" }
  | { type: "bondsProduct" }
  | { type: "blogEdit"; slug: string }
  | { type: "blogArticle"; slug: string }
  | { type: "v2"; route: string }
  | { type: "v2Index" }
  | { type: "notFound" };

/* Short, clean browser-tab titles per route (format: "<Name> | Choice Wealth").
   Only overrides the visible <title>; SEO description/keywords stay long. */
const SHORT_TITLES: Partial<Record<RouteMatch["type"], string>> = {
  modernHomeThree: "Choice Wealth",
  blogIndex: "Blog | Choice Wealth",
  blogManage: "Manage Blogs | Choice Wealth",
  adminLogin: "Admin Login | Choice Wealth",
  blogCreate: "Create Blog | Choice Wealth",
  blogPreview: "Preview Blog | Choice Wealth",
  blogEdit: "Edit Blog | Choice Wealth",
  setupCall: "Setup a Call | Choice Wealth",
  setupCallMinimal: "Setup a Call | Choice Wealth",
  productsHub: "Products | Choice Wealth",
  servicesHub: "Services | Choice Wealth",
  whoWeServe: "Who We Serve | Choice Wealth",
  privacyPolicy: "Privacy Policy | Choice Wealth",
  termsAndConditions: "Terms & Conditions | Choice Wealth",
  board: "Board Members | Choice Wealth",
  boardMembersPageTwo: "Group | Choice Wealth",
  aboutUs: "About Us | Choice Wealth",
  ourPhilosophy: "Our Philosophy | Choice Wealth",
  coreTeam: "Core Team | Choice Wealth",
  wealthManagers: "Wealth Managers | Choice Wealth",
  contactUs: "Contact Us | Choice Wealth",
  portfolioHealthReview: "Portfolio Health Review | Choice Wealth",
  retirementWealthReview: "Retirement Wealth Review | Choice Wealth",
  structuredWealthReview: "Structured Wealth Review | Choice Wealth",
  globalInvestmentSolutions: "Global Investment Solutions | Choice Wealth",
  taxAwarePortfolioReview: "Tax-Aware Portfolio Review | Choice Wealth",
  legacyWealthTransferCoordination: "Legacy & Wealth Transfer | Choice Wealth",
  whoWeServeHniPrivateWealth: "HNI Private Wealth | Choice Wealth",
  whoWeServeCorporateTreasury: "Corporate Treasury | Choice Wealth",
  whoWeServeBusinessOwnersPromoters: "Business Owners & Promoters | Choice Wealth",
  whoWeServeFamilyOffice: "Family Office | Choice Wealth",
  whoWeServeNriGlobalIndianWealth: "NRI & Global Indian Wealth | Choice Wealth",
  whoWeServePrivateClientWealth: "Private Client Wealth | Choice Wealth",
  whoWeServeCxosSeniorProfessionals: "CXOs & Senior Professionals | Choice Wealth",
  whoWeServeFoundersEntrepreneurs: "Founders & Entrepreneurs | Choice Wealth",
  mutualFundProduct: "Mutual Funds | Choice Wealth",
  npsProduct: "NPS | Choice Wealth",
  aifProduct: "AIF | Choice Wealth",
  mldProduct: "MLD | Choice Wealth",
  corporateFdTwo: "Corporate FD | Choice Wealth",
  bondsProduct: "Bonds | Choice Wealth",
  portfolioManagementServicesTwo: "PMS | Choice Wealth",
  sifProduct: "SIF | Choice Wealth",
  ncdProduct: "NCD | Choice Wealth",
  fixedIncomeProduct: "Fixed Income | Choice Wealth",
  notFound: "404 | Choice Wealth",
};

function normalizeRoutePath(pathname: string) {
  return pathname.replace(/\/$/, "") || "/";
}

// Legacy paths that should permanently resolve to a new canonical URL.
// The old path still renders (alias in matchRoute) so there is no 404 flash;
// the redirect effect then rewrites the address bar to the canonical URL.
// Every non-canonical alias resolves to the new flat canonical slug from the SEO
// sheet (New_Final_URL_Path). The redirect effect rewrites the address bar to the
// destination, so any old /product/… /services/… /who-we-serve/… URL — including
// stale internal links — lands on the canonical URL that matches the <link rel=canonical>.
const LEGACY_REDIRECTS: Record<string, string> = {
  // Products → new flat canonical slugs
  "/product/portfolio-management-services": "/pms-portfolio-management-services",
  "/products/portfolio-management-services": "/pms-portfolio-management-services",
  "/portfolio-management-services": "/pms-portfolio-management-services",
  "/portfolio-management-services-2": "/pms-portfolio-management-services",

  "/product/alternative-investment-funds": "/aif-alternative-investment-funds",
  "/products/alternative-investment-funds": "/aif-alternative-investment-funds",
  "/alternative-investment-funds": "/aif-alternative-investment-funds",
  "/aif-portfolio-review": "/aif-alternative-investment-funds",

  "/product/mutual-fund": "/mutual-funds-advisory",
  "/products/mutual-funds": "/mutual-funds-advisory",
  "/mutual-fund-investment-services": "/mutual-funds-advisory",
  "/mutual-funds-portfolio-review": "/mutual-funds-advisory",

  "/product/bonds-investment-services": "/bonds-investment",
  "/products/bonds": "/bonds-investment",
  "/bonds-investment-services": "/bonds-investment",
  "/bond-portfolio-review": "/bonds-investment",

  "/product/non-convertible-debentures": "/ncd-non-convertible-debentures",
  "/products/non-convertible-debentures": "/ncd-non-convertible-debentures",
  "/non-convertible-debentures": "/ncd-non-convertible-debentures",
  "/ncd": "/ncd-non-convertible-debentures",

  "/product/corporate-fd": "/corporate-fixed-deposits",
  "/products/corporate-fixed-deposits": "/corporate-fixed-deposits",
  "/corporate-fd-review": "/corporate-fixed-deposits",

  "/product/market-linked-debentures": "/mld-market-linked-debentures",
  "/products/market-linked-debentures": "/mld-market-linked-debentures",
  "/market-linked-debentures": "/mld-market-linked-debentures",
  "/mld-review": "/mld-market-linked-debentures",

  "/product/specialised-investment-funds": "/sif-specialised-investment-funds",
  "/product/specialized-investment-funds": "/sif-specialised-investment-funds",
  "/products/special-investment-funds": "/sif-specialised-investment-funds",
  "/specialised-investment-funds": "/sif-specialised-investment-funds",
  "/sif": "/sif-specialised-investment-funds",

  "/product/nps": "/nps-national-pension-system",
  "/product/national-pension-system": "/nps-national-pension-system",
  "/products/national-pension-system": "/nps-national-pension-system",
  "/nps-investment": "/nps-national-pension-system",
  "/national-pension-system-investment": "/nps-national-pension-system",
  "/nps": "/nps-national-pension-system",

  "/product/fixed-income": "/fixed-income-investments",
  "/fixed-income-investment-services": "/fixed-income-investments",
  "/fixed-income-review": "/fixed-income-investments",

  // Services → new flat canonical slugs
  "/services/portfolio-health-review": "/portfolio-health-review",
  "/services/structured-wealth-review": "/structured-wealth-review",
  "/services/tax-aware-portfolio-review": "/tax-aware-portfolio-review",
  "/services/retirement-wealth-review": "/retirement-wealth-review",
  "/services/legacy-wealth-transfer-coordination": "/legacy-wealth-transfer-coordination",
  "/services/global-investment-solutions": "/global-investment-solutions",

  // Who We Serve → new flat canonical slugs
  "/who-we-serve/hni-private-wealth-management": "/hni-private-wealth-management",
  "/who-we-serve/nri-global-indian-wealth-management": "/nri-global-indian-wealth-management",
  "/who-we-serve/corporate-treasury-solutions": "/corporate-treasury-solutions",
  "/who-we-serve/family-office-business-families": "/family-office-business-families",
  "/who-we-serve/business-owners-promoters": "/business-owners-promoters",
  "/who-we-serve/founders-entrepreneurs": "/founders-entrepreneurs",
  "/who-we-serve/cxos-senior-professionals": "/cxos-senior-professionals",
  "/who-we-serve/private-client-wealth": "/private-client-wealth",

  // Legacy about page
  "/about-us": "/our-story",
};

const V2_PRIMARY_ROUTE_ALIASES: Record<string, string> = {
};

function matchRoute(pathname: string): RouteMatch {
  const normalizedPathname = normalizeRoutePath(pathname);

  if (normalizedPathname === "/") {
    return { type: "modernHomeThree" };
  }

  if (
    normalizedPathname === "/pms-portfolio-management-services" ||
    normalizedPathname === "/product/portfolio-management-services" ||
    normalizedPathname === "/portfolio-management-services-2" ||
    normalizedPathname === "/portfolio-management-services"
  ) {
    return { type: "portfolioManagementServicesTwo" };
  }

  if (
    normalizedPathname === "/corporate-fixed-deposits" ||
    normalizedPathname === "/product/corporate-fd" ||
    normalizedPathname === "/corporate-fd-review"
  ) {
    return { type: "corporateFdTwo" };
  }

  if (
    normalizedPathname === "/fixed-income-investments" ||
    normalizedPathname === "/product/fixed-income" ||
    normalizedPathname === "/fixed-income-investment-services" ||
    normalizedPathname === "/fixed-income-review"
  ) {
    return { type: "fixedIncomeProduct" };
  }

  if (
    normalizedPathname === "/mld-market-linked-debentures" ||
    normalizedPathname === "/product/market-linked-debentures" ||
    normalizedPathname === "/market-linked-debentures" ||
    normalizedPathname === "/mld-review"
  ) {
    return { type: "mldProduct" };
  }

  if (
    normalizedPathname === "/mutual-funds-advisory" ||
    normalizedPathname === "/product/mutual-fund" ||
    normalizedPathname === "/mutual-fund-investment-services" ||
    normalizedPathname === "/mutual-funds-portfolio-review"
  ) {
    return { type: "mutualFundProduct" };
  }

  if (
    normalizedPathname === "/aif-alternative-investment-funds" ||
    normalizedPathname === "/product/alternative-investment-funds" ||
    normalizedPathname === "/alternative-investment-funds" ||
    normalizedPathname === "/aif-portfolio-review"
  ) {
    return { type: "aifProduct" };
  }

  if (
    normalizedPathname === "/sif-specialised-investment-funds" ||
    normalizedPathname === "/product/specialised-investment-funds" ||
    normalizedPathname === "/product/specialized-investment-funds" ||
    normalizedPathname === "/specialised-investment-funds" ||
    normalizedPathname === "/sif"
  ) {
    return { type: "sifProduct" };
  }

  if (
    normalizedPathname === "/ncd-non-convertible-debentures" ||
    normalizedPathname === "/product/non-convertible-debentures" ||
    normalizedPathname === "/non-convertible-debentures" ||
    normalizedPathname === "/ncd"
  ) {
    return { type: "ncdProduct" };
  }

  if (
    normalizedPathname === "/nps-national-pension-system" ||
    normalizedPathname === "/product/nps" ||
    normalizedPathname === "/product/national-pension-system" ||
    normalizedPathname === "/nps-investment" ||
    normalizedPathname === "/national-pension-system-investment" ||
    normalizedPathname === "/nps"
  ) {
    return { type: "npsProduct" };
  }

  if (
    normalizedPathname === "/bonds-investment" ||
    normalizedPathname === "/product/bonds-investment-services" ||
    normalizedPathname === "/bonds-investment-services" ||
    normalizedPathname === "/bond-portfolio-review"
  ) {
    return { type: "bondsProduct" };
  }

  if (
    normalizedPathname === "/portfolio-health-review" ||
    normalizedPathname === "/services/portfolio-health-review"
  ) {
    return { type: "portfolioHealthReview" };
  }

  if (
    normalizedPathname === "/retirement-wealth-review" ||
    normalizedPathname === "/services/retirement-wealth-review"
  ) {
    return { type: "retirementWealthReview" };
  }

  if (
    normalizedPathname === "/structured-wealth-review" ||
    normalizedPathname === "/services/structured-wealth-review"
  ) {
    return { type: "structuredWealthReview" };
  }

  if (
    normalizedPathname === "/global-investment-solutions" ||
    normalizedPathname === "/services/global-investment-solutions"
  ) {
    return { type: "globalInvestmentSolutions" };
  }

  if (
    normalizedPathname === "/tax-aware-portfolio-review" ||
    normalizedPathname === "/services/tax-aware-portfolio-review"
  ) {
    return { type: "taxAwarePortfolioReview" };
  }

  if (
    normalizedPathname === "/legacy-wealth-transfer-coordination" ||
    normalizedPathname === "/services/legacy-wealth-transfer-coordination"
  ) {
    return { type: "legacyWealthTransferCoordination" };
  }

  const primaryV2Route = V2_PRIMARY_ROUTE_ALIASES[normalizedPathname];

  if (primaryV2Route) {
    return { type: "v2", route: primaryV2Route };
  }

  if (normalizedPathname === "/home-3") {
    return { type: "modernHomeThree" };
  }

  if (normalizedPathname === "/blog") {
    return { type: "blogIndex" };
  }

  if (normalizedPathname === "/blog/manage") {
    return { type: "blogManage" };
  }

  if (normalizedPathname === "/admin/login") {
    return { type: "adminLogin" };
  }

  if (normalizedPathname === "/blog/create") {
    return { type: "blogCreate" };
  }

  if (normalizedPathname === "/blog/preview") {
    return { type: "blogPreview" };
  }

  if (normalizedPathname === "/setup-call") {
    return { type: "setupCall" };
  }

  if (normalizedPathname === "/setup-call-minimal") {
    return { type: "setupCallMinimal" };
  }

  if (normalizedPathname === "/products") {
    return { type: "productsHub" };
  }

  if (normalizedPathname === "/privacy-policy") {
    return { type: "privacyPolicy" };
  }

  if (normalizedPathname === "/terms-and-conditions") {
    return { type: "termsAndConditions" };
  }

  if (normalizedPathname === "/group") {
    return { type: "boardMembersPageTwo" };
  }

  if (normalizedPathname === "/our-story" || normalizedPathname === "/about-us") {
    return { type: "aboutUs" };
  }

  if (normalizedPathname === "/our-philosophy") {
    return { type: "ourPhilosophy" };
  }

  if (normalizedPathname === "/core-team") {
    return { type: "coreTeam" };
  }

  if (normalizedPathname === "/board-members") {
    return { type: "board" };
  }

  if (normalizedPathname === "/wealth-managers") {
    return { type: "wealthManagers" };
  }

  if (normalizedPathname === "/contact-us") {
    return { type: "contactUs" };
  }

  if (normalizedPathname === "/services") {
    return { type: "servicesHub" };
  }

  const serviceDetailMatch = pathname.match(/^\/services\/([^/]+)\/?$/);
  if (normalizedPathname === "/who-we-serve") {
    return { type: "whoWeServe" };
  }

  if (normalizedPathname === "/who-we-serve/hni-private-wealth-management") {
    return { type: "whoWeServeHniPrivateWealth" };
  }

  if (normalizedPathname === "/hni-private-wealth-management") {
    return { type: "whoWeServeHniPrivateWealth" };
  }

  if (normalizedPathname === "/who-we-serve/nri-global-indian-wealth-management") {
    return { type: "whoWeServeNriGlobalIndianWealth" };
  }

  if (normalizedPathname === "/who-we-serve/corporate-treasury-solutions") {
    return { type: "whoWeServeCorporateTreasury" };
  }

  if (normalizedPathname === "/corporate-treasury-solutions") {
    return { type: "whoWeServeCorporateTreasury" };
  }

  if (normalizedPathname === "/who-we-serve/business-owners-promoters") {
    return { type: "whoWeServeBusinessOwnersPromoters" };
  }

  if (normalizedPathname === "/business-owners-promoters") {
    return { type: "whoWeServeBusinessOwnersPromoters" };
  }

  if (normalizedPathname === "/who-we-serve/family-office-business-families") {
    return { type: "whoWeServeFamilyOffice" };
  }

  if (normalizedPathname === "/family-office-business-families") {
    return { type: "whoWeServeFamilyOffice" };
  }

  if (normalizedPathname === "/nri-global-indian-wealth-management") {
    return { type: "whoWeServeNriGlobalIndianWealth" };
  }

  if (normalizedPathname === "/who-we-serve/private-client-wealth") {
    return { type: "whoWeServePrivateClientWealth" };
  }

  if (normalizedPathname === "/private-client-wealth") {
    return { type: "whoWeServePrivateClientWealth" };
  }

  if (normalizedPathname === "/who-we-serve/cxos-senior-professionals") {
    return { type: "whoWeServeCxosSeniorProfessionals" };
  }

  if (normalizedPathname === "/cxos-senior-professionals") {
    return { type: "whoWeServeCxosSeniorProfessionals" };
  }

  if (normalizedPathname === "/who-we-serve/founders-entrepreneurs") {
    return { type: "whoWeServeFoundersEntrepreneurs" };
  }

  if (normalizedPathname === "/founders-entrepreneurs") {
    return { type: "whoWeServeFoundersEntrepreneurs" };
  }

  const editMatch = pathname.match(/^\/blog\/edit\/([^/]+)$/);
  if (editMatch) {
    return { type: "blogEdit", slug: decodeURIComponent(editMatch[1]) };
  }

  const blogMatch = pathname.match(/^\/blog\/([^/]+)$/);
  if (blogMatch) {
    return { type: "blogArticle", slug: decodeURIComponent(blogMatch[1]) };
  }

  if (normalizedPathname === "/v2") {
    return { type: "v2Index" };
  }

  const v2Path = normalizedPathname;
  if (/-2$/.test(v2Path) || v2Path === "/ncd-investment-services") {
    return { type: "v2", route: v2Path };
  }

  return { type: "notFound" };
}

function ensureMeta(name: string, content: string, property = false) {
  const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
  let node = document.head.querySelector<HTMLMetaElement>(selector);
  if (!node) {
    node = document.createElement("meta");
    node.setAttribute(property ? "property" : "name", name);
    document.head.appendChild(node);
  }
  node.content = content;
}

/** Current origin with any leading "www." removed — non-www is the preferred host. */
function getPreferredOrigin(): string {
  return window.location.origin.replace(/^(https?:\/\/)www\./i, "$1");
}

/**
 * Rebase an absolute canonical onto the host that actually served the page.
 * The SEO sheet stores production canonicals (https://choicewealth.in/<path>);
 * this keeps the exact path (incl. any trailing slash) but swaps scheme+host for
 * the current origin, so each environment emits its own host automatically with no
 * per-environment build config: live -> https://choicewealth.in/<path>, uat ->
 * https://uat.choicewealth.in/<path>, dev -> https://dev.choicewealth.in/<path>,
 * local -> http://localhost:.../<path>.
 */
function rebaseToCurrentOrigin(canonicalUrl: string): string {
  if (typeof window === "undefined") {
    return canonicalUrl;
  }
  try {
    const parsed = new URL(canonicalUrl);
    return `${getPreferredOrigin()}${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return canonicalUrl;
  }
}

function ensureLink(rel: string, href: string) {
  let node = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!node) {
    node = document.createElement("link");
    node.rel = rel;
    document.head.appendChild(node);
  }
  node.href = href;
}

function ensureJsonLd(id: string, payload: object | null) {
  let node = document.head.querySelector<HTMLScriptElement>(`script[data-schema-id="${id}"]`);

  if (!payload) {
    node?.remove();
    return;
  }

  if (!node) {
    node = document.createElement("script");
    node.type = "application/ld+json";
    node.dataset.schemaId = id;
    document.head.appendChild(node);
  }

  node.textContent = JSON.stringify(payload);
}

// Single shared loader for the whole app. See src/components/common/PageLoader.tsx.
function PageContentFallback() {
  return <PageLoader />;
}

/** Reset the window to the very top. On capable desktops Lenis smooth scroll owns
 * the scroll position and its RAF loop overrides a plain window.scrollTo, so drive
 * Lenis directly (same pattern as the scroll-to-top button) with native fallbacks. */
function scrollWindowToTop() {
  if (typeof window === "undefined") {
    return;
  }
  const lenis = (window as unknown as {
    __lenis?: { scrollTo: (target: number, opts?: unknown) => void };
  }).__lenis;
  if (lenis) {
    lenis.scrollTo(0, { immediate: true, force: true });
  }
  window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
}

/**
 * Rendered INSIDE the Suspense boundary next to the page. Because Suspense does
 * not mount its children until the lazy chunk has resolved, this component's
 * mount effect is a reliable "the new page content is now in the DOM" signal.
 * We wait two frames (so layout/pins have painted), reset scroll, then tell the
 * app the route is ready — which drops the covering loader.
 */
function PageReadySignal({ routeKey, onReady }: { routeKey: string; onReady: () => void }) {
  useEffect(() => {
    let cancelled = false;
    let raf1 = 0;
    let raf2 = 0;
    let fallback = 0;

    const finish = () => {
      if (cancelled) {
        return;
      }
      cancelled = true;
      window.removeEventListener("cw:page-revealed", finish);
      window.clearTimeout(fallback);
      raf1 = requestAnimationFrame(() => {
        scrollWindowToTop();
        raf2 = requestAnimationFrame(() => {
          scrollWindowToTop();
          onReady();
        });
      });
    };

    // Pages built with useChoreography keep their hero hidden until the shared
    // reveal runs, and those reveals are MASKED — content is invisible until the
    // tween plays. Lifting the loader on a bare frame timer therefore uncovers a
    // still-empty hero (the blank light screen with a faint logo). Instead the
    // choreography fires `cw:page-revealed` once real content is on screen (see
    // src/motion/motionCore.ts), and we hand off then. A short fallback covers
    // pages that don't run choreography, and the reveal is gated on fonts.ready so
    // we don't listen before the page has had a chance to set itself up.
    // Listen IMMEDIATELY. On phones the choreography takes the static-hero path and
    // fires `cw:page-revealed` without waiting for fonts (see motionCore.ts), so an
    // arm-after-fonts.ready listener would miss the event outright and leave the loader
    // up until the fallback fired. The fallback timer stays behind fonts.ready so that
    // capable devices, whose masked reveal legitimately runs ~1.5s, are not uncovered
    // early onto a still-empty hero.
    window.addEventListener("cw:page-revealed", finish);

    const fontsReady =
      typeof document !== "undefined" && "fonts" in document
        ? document.fonts.ready.catch(() => undefined)
        : Promise.resolve();

    void fontsReady.then(() => {
      if (cancelled) {
        return;
      }
      fallback = window.setTimeout(finish, 900);
    });

    return () => {
      cancelled = true;
      window.removeEventListener("cw:page-revealed", finish);
      window.clearTimeout(fallback);
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [routeKey, onReady]);

  return null;
}

function App() {
  const { isAuthenticated } = useAuth();
  const pathname = usePathname();
  const route = useMemo(() => matchRoute(pathname), [pathname]);
  // On the home route, defer mounting the (framer-motion-heavy) page body until after the
  // eager hero has painted, so it doesn't contend with the hero's LCP.
  const homeBodyReady = useDeferredMount();
  const [seoBlog, setSeoBlog] = useState<BlogDocument | null>(null);
  const previousPathnameRef = useRef(pathname);
  // Which route's content has finished mounting + settling. While this lags the
  // current pathname, we keep a full-screen loader over everything so the always-
  // mounted footer can never flash before the new page's hero is in place.
  //
  // On a COLD load (refresh / direct URL) we start this null for non-home routes so
  // the top-level loader covers the initial chunk load too. Otherwise only the lazy
  // Suspense fallback would show — and that lives inside AppLayout's route-enter
  // transform, which breaks position:fixed and lets the light page (faint logo)
  // leak through on refresh. Home starts ready so its eager hero paints for LCP.
  const [readyPath, setReadyPath] = useState<string | null>(() => {
    // Prerendered routes ship their real content inside #root, so there is nothing to
    // wait for and no blank frame to hide — starting "not ready" would drop the cold-
    // load cover over markup that is already painted, which is exactly the LCP delay
    // prerendering exists to remove. `typeof window === "undefined"` covers the build
    // pass itself so the emitted HTML never contains the loader.
    if (typeof window === "undefined" || window.__CW_PRERENDERED__) {
      return pathname;
    }
    const initialRoute = matchRoute(pathname);
    return initialRoute.type === "modernHomeThree" ? pathname : null;
  });
  const handleRouteReady = useMemo(() => () => setReadyPath(pathname), [pathname]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    window.history.scrollRestoration = "manual";
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const target = LEGACY_REDIRECTS[normalizeRoutePath(pathname)];
    if (target && normalizeRoutePath(pathname) !== normalizeRoutePath(target)) {
      window.history.replaceState({}, "", `${target}${window.location.search}${window.location.hash}`);
      window.dispatchEvent(new PopStateEvent("popstate"));
    }
  }, [pathname]);

  useEffect(() => {
    persistLeadTrackingFromLocation(typeof window !== "undefined" ? window.location.href : "");
  }, [pathname]);

  useEffect(() => {
    if (previousPathnameRef.current === pathname) {
      return;
    }

    previousPathnameRef.current = pathname;

    // Cover everything with the loader until the new page reports ready (see
    // PageReadySignal). This closes the gap where the always-mounted footer would
    // otherwise peek through while the lazy chunk loads and scroll settles.
    setReadyPath((current) => (current === pathname ? current : null));

    // Reset scroll immediately too. On capable desktops Lenis smooth scroll owns
    // the scroll position and its RAF loop overrides a plain window.scrollTo, so
    // scrollWindowToTop drives Lenis directly; re-assert across a couple of frames
    // so the reset survives the transition and Lenis' ticker.
    scrollWindowToTop();
    const raf1 = requestAnimationFrame(scrollWindowToTop);
    const raf2 = requestAnimationFrame(() => {
      requestAnimationFrame(scrollWindowToTop);
    });
    // Safety net: never trap the loader if a page never fires its ready signal.
    const failSafe = window.setTimeout(() => setReadyPath(pathname), 4000);

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      window.clearTimeout(failSafe);
    };
  }, [pathname]);

  useEffect(() => {
    if (pathname !== "/setup-call") {
      return;
    }

    window.history.replaceState({}, "", "/setup-call-minimal");
    window.dispatchEvent(new PopStateEvent("popstate"));
  }, [pathname]);

  useEffect(() => {
    let active = true;

    if (route.type !== "blogArticle") {
      setSeoBlog(null);
      return () => {
        active = false;
      };
    }

    void fetchBlogBySlug(route.slug).then((result) => {
      if (!active) {
        return;
      }
      setSeoBlog(result.data);
    });

    return () => {
      active = false;
    };
  }, [route]);

  useEffect(() => {
    const origin = typeof window === "undefined" ? "https://example.com" : getPreferredOrigin();
    const canonicalUrl = `${origin}${pathname}`;

    const defaultTitle = "Choice Wealth PMS | Managed Equity Exposure For Serious Portfolios";
    const defaultDescription =
      "A premium Choice Wealth PMS landing experience focused on portfolio-first evaluation, manager process review, strategy fit, and disclosure visibility.";
    const defaultKeywords =
      "Choice Wealth PMS, portfolio management services, managed equity, PMS review, wealth management";

    let title = defaultTitle;
    let description = defaultDescription;
    let keywords = defaultKeywords;
    let ogType = "website";
    let articleSchema: object | null = null;

    if (route.type === "modernHomeThree") {
      title = "Choice Wealth - Where Wealth Comes Home";
      description =
        "Choice Wealth helps serious investors review, structure and monitor capital through RM-led wealth conversations backed by the Choice Group ecosystem. Request a Wealth Review.";
      keywords =
        "Choice Wealth, wealth management in India, private wealth management, portfolio review, wealth management services";
    } else if (route.type === "blogIndex") {
      title = "Choice Wealth Blog | PMS, Portfolio Review And Manager Research";
      description =
        "Animated longform blog routes for PMS analysis, manager review, portfolio architecture, and generated HTML-driven editorial content.";
      keywords =
        "PMS blog, portfolio review blog, Choice Wealth blog, manager research, portfolio architecture";
    } else if (route.type === "blogManage") {
      title = "Manage Blogs | Choice Wealth";
      description = "Manage draft and published blog articles.";
      keywords = "manage blogs, editorial console, Choice Wealth";
    } else if (route.type === "adminLogin") {
      title = "Admin Login | Choice Wealth";
      description = "Admin login for blog creation and editorial management.";
      keywords = "admin login, Choice Wealth";
    } else if (route.type === "blogCreate") {
      title = "Create Blog Route | Choice Wealth Editorial System";
      description =
        "Internal-style generator page for creating blog entries that map directly to the /blog/:slug route.";
      keywords = "blog generator, editorial route, generated html blog, Choice Wealth";
    } else if (route.type === "blogPreview") {
      title = "Preview Blog Draft | Choice Wealth Editorial System";
      description = "Preview the current draft before publishing it.";
      keywords = "blog preview, draft preview, Choice Wealth";
    } else if (route.type === "setupCall") {
      title = "Setup a Call | Choice Wealth Private Consultation";
      description =
        "Schedule a private consultation with Choice Wealth to discuss wealth planning, portfolio review, PMS guidance, and long-term investment direction.";
      keywords =
        "setup a call, private consultation, wealth planning call, portfolio review, Choice Wealth";
    } else if (route.type === "setupCallMinimal") {
      title = "Setup a Call Minimal | Choice Wealth";
      description =
        "A minimal line-art setup call page for scheduling a private wealth conversation around your goals and next investment steps.";
      keywords =
        "setup a call minimal, private consultation, wealth planning conversation, Choice Wealth";
    } else if (route.type === "productsHub") {
      title = "Investment Products in India | Product Access Universe | Choice Wealth";
      description =
        "Explore Choice Wealth product-access routes across PMS, Mutual Funds, Fixed Income, Bonds, NPS, AIFs, MLDs and Corporate FDs. Start with role, fit and review context before access is discussed.";
      keywords =
        "investment products India, portfolio management services India, mutual funds India, fixed income products India, bonds India, NPS India, AIF India, MLD India, corporate fixed deposits, Choice Wealth products";
    } else if (route.type === "privacyPolicy") {
      title = "Privacy Policy | Choice Wealth";
      description =
        "Read the Choice Wealth Privacy Policy covering data collection, KYC data use, cookies, data retention, disclosure, security measures and privacy rights.";
      keywords =
        "Choice Wealth privacy policy, data privacy, KYC data policy, cookies policy, personal information, data retention, wealth management privacy";
    } else if (route.type === "termsAndConditions") {
      title = "Terms & Conditions | Choice Wealth";
      description =
        "Read the Choice Wealth Terms & Conditions covering client obligations, credential security, monitoring, surveillance, investment risk disclosure and dispute resolution.";
      keywords =
        "Choice Wealth terms and conditions, client obligations, investment risk disclosure, governing law, surveillance, identity protection";
    } else if (route.type === "boardMembersPageTwo") {
      title = "Choice Group - Built on Trust, Delivering Excellence";
      description =
        "Choice Group is a listed, diversified financial services ecosystem — spanning wealth, broking, NBFC lending, insurance and more — built on over a decade of proven expertise across Bharat.";
      keywords =
        "Choice Group, Choice International, Choice Wealth, financial services ecosystem, Choice FinX, Choice Money, business ecosystem";
    } else if (route.type === "aboutUs") {
      title = "About Choice Wealth | 100+ Years Of Combined Financial Experience";
      description =
        "Backed by decades of collective market experience, Choice Wealth brings together capabilities across public markets, fixed income, alternative investments and private assets through a disciplined, portfolio-led approach to wealth management.";
      keywords =
        "About Choice Wealth, Choice Wealth method, why Choice Wealth exists, Choice Wealth values, Choice Wealth leadership, Choice Equity Broking";
    } else if (route.type === "ourPhilosophy") {
      title = "Our Philosophy — The Choice Way | Choice Wealth";
      description =
        "The Choice Way: wealth built through thoughtful allocation, disciplined decisions and patience across market cycles. Explore the principles that guide Choice Wealth's thinking.";
      keywords =
        "Choice Wealth philosophy, The Choice Way, wealth allocation, investment principles, diversification, risk perspective";
    } else if (route.type === "coreTeam") {
      title = "Core Team | Choice Wealth";
      description =
        "The team behind the wealth conversation — leaders across private client relationships, HNI desks, research, fixed income, treasury, operations and finance at Choice Wealth.";
      keywords =
        "Choice Wealth core team, wealth management team, PCG desk, HNI desk, research and products, Choice Wealth leadership";
    } else if (route.type === "board") {
      title = "Board Members | Choice Wealth";
      description =
        "Guided by experience, focused on stewardship. Meet the Board behind Choice Wealth and the direction, governance and continuity they provide.";
      keywords =
        "Choice Wealth board members, board of directors, Kamal Poddar, Ajay Kejriwal, Arun Poddar, Suyash Patodia, governance";
    } else if (route.type === "wealthManagers") {
      title = "Wealth Managers | Choice Wealth";
      description =
        "For wealth that deserves ongoing attention. Work with a dedicated team of Choice Wealth managers who begin every conversation with allocation, liquidity and long-term objectives.";
      keywords =
        "Choice Wealth managers, private wealth managers, wealth management team, relationship managers, portfolio management";
    } else if (route.type === "contactUs") {
      title = "Contact Us | Choice Wealth";
      description =
        "#MakeTheRightChoice for your wealth. Connect with the Choice Wealth team to discuss your financial objectives and explore investment options.";
      keywords =
        "contact Choice Wealth, wealth desk, schedule financial review, Choice Wealth Mumbai, Choice Wealth phone email";
    } else if (route.type === "servicesHub") {
      title = "Wealth Management Services India | Review-Led Services | Choice Wealth";
      description =
        "Explore Choice Wealth services across portfolio health, structured wealth review, retirement, tax-aware context, legacy coordination, global investments, liquidity and review rhythm. Start with a review-led conversation.";
      keywords =
        "wealth management services India, private wealth services India, portfolio review services India, structured wealth services, wealth review services, Choice Wealth services";
    } else if (route.type === "portfolioHealthReview") {
      title = "Portfolio Health Review | Choice Wealth";
      description =
        "Review risk, liquidity, concentration, overlap and review rhythm inside your current portfolio with Choice Wealth before new product access is discussed.";
      keywords =
        "portfolio health review, portfolio review india, portfolio overlap review, risk and liquidity review, choice wealth portfolio review";
    } else if (route.type === "retirementWealthReview") {
      title = "Retirement Wealth Review | Choice Wealth";
      description =
        "Review retirement readiness across corpus, income, liquidity, NPS, fixed income, mutual funds, family context and review rhythm with Choice Wealth.";
      keywords =
        "retirement planning india, retirement wealth management, retirement corpus planning, retirement income planning, retirement portfolio review";
    } else if (route.type === "structuredWealthReview") {
      title = "Structured Wealth Review | Choice Wealth";
      description =
        "Review capital across goals, risk, liquidity, product roles and review rhythm with Choice Wealth. Start a structured wealth review before product access is discussed.";
      keywords =
        "structured wealth planning, wealth structuring, structured wealth management, wealth review services, wealth management process india";
    } else if (route.type === "globalInvestmentSolutions") {
      title = "Global Investment Solutions | Choice Wealth";
      description =
        "Review global allocation context across residency, documentation, currency, jurisdiction, eligibility, risk and portfolio role with Choice Wealth before cross-border access is discussed.";
      keywords =
        "global investment solutions india, overseas investment options india, gift city investments, global portfolio diversification, nri global investments";
    } else if (route.type === "taxAwarePortfolioReview") {
      title = "Tax-Aware Portfolio Review | Choice Wealth";
      description =
        "Review where tax context may matter across holdings, exits, payouts, residency, documentation and product decisions. Tax matters should be reviewed with qualified professionals.";
      keywords =
        "tax-aware portfolio review, portfolio tax review, capital gains context, tax-aware investing india, investment portfolio tax review";
    } else if (route.type === "legacyWealthTransferCoordination") {
      title = "Legacy & Wealth Transfer Coordination | Choice Wealth";
      description =
        "Review family wealth context across ownership, nominations, product holdings, business-linked wealth and professional coordination needs. Choice Wealth does not provide legal, estate or succession advice.";
      keywords =
        "legacy wealth transfer coordination, family wealth transfer review, estate planning context india, succession planning context, choice wealth legacy coordination";
    } else if (route.type === "whoWeServe") {
      title = "Who We Serve | Choice Wealth Client Types and Wealth Starting Points";
      description =
        "Find the right Choice Wealth starting point by capital situation. Explore HNI wealth, NRI wealth, corporate treasury, family office, founders, CXOs and private client routes.";
      keywords =
        "Choice Wealth who we serve, Choice Wealth client types, wealth management services by client type, who does Choice Wealth serve, HNI wealth management Choice Wealth, NRI wealth management Choice Wealth, corporate treasury solutions Choice Wealth, family office services Choice Wealth, private client wealth Choice Wealth";
    } else if (route.type === "portfolioManagementServicesTwo") {
      title = "PMS for High-Conviction Investing — Choice Wealth";
      description =
        "Focused portfolios managed with a disciplined investment approach for investors seeking differentiated equity exposure.";
      keywords =
        "Portfolio Management Services, PMS investment in India, PMS portfolio review, Choice Wealth PMS, managed equity exposure, PMS suitability review";
    } else if (route.type === "corporateFdTwo") {
      title = "Corporate Fixed Deposits — Reviewed Beyond the Rate | Choice Wealth";
      description =
        "Issuer-backed Corporate Fixed Deposits reviewed by rate, credit rating, tenure, payout option, premature withdrawal terms, tax impact and portfolio fit before allocation.";
      keywords =
        "Corporate Fixed Deposits, corporate FD, corporate FD rates, corporate fd vs bank fd, corporate fd taxation, Choice Wealth fixed income, issuer-backed deposit review";
    } else if (route.type === "fixedIncomeProduct") {
      title = "Fixed Income, Structured Around What Your Capital Needs To Do — Choice Wealth";
      description =
        "Build the income, liquidity and stability layer of your portfolio across deposits, bonds, NCDs, G-Secs, debt funds and MLDs — reviewed by issuer quality, tenure, liquidity, tax and portfolio role.";
      keywords =
        "fixed income, fixed income investments, bonds, NCDs, G-Secs, T-Bills, SDLs, debt mutual funds, MLDs, 54EC bonds, Choice Wealth fixed income review";
    } else if (route.type === "mldProduct") {
      title = "Market Linked Debentures — Structured Investing With Greater Intent | Choice Wealth";
      description =
        "Structured debt instruments where payoff is linked to defined market conditions. Review issuer quality, payoff rules, tenure, taxation and portfolio fit before you invest.";
      keywords =
        "Market Linked Debentures, MLD, structured debt, MLD payoff structure, MLD vs NCD, MLD taxation, Choice Wealth MLD review";
    } else if (route.type === "mutualFundProduct") {
      title = "Mutual Funds — The Core of Every Well-Built Portfolio | Choice Wealth";
      description =
        "Mutual funds provide a professionally managed foundation for long-term investing. Review fund categories, overlap, role and portfolio fit with Choice Wealth.";
      keywords =
        "Mutual Funds, SIP, ELSS, equity funds, debt funds, hybrid funds, index funds, mutual fund portfolio review, Choice Wealth mutual funds";
    } else if (route.type === "aifProduct") {
      title = "Alternative Investment Funds (AIF) — Beyond Traditional Markets | Choice Wealth";
      description =
        "Explore AIFs with a portfolio-first review across Category I, II and III. Evaluate category fit, manager, risk, liquidity, fees, tax and overlap with Choice Wealth.";
      keywords =
        "Alternative Investment Funds, AIF, AIF Category I II III, AIF vs PMS, AIF portfolio review, private markets, Choice Wealth AIF";
    } else if (route.type === "bondsProduct") {
      title = "Bonds That Balance Growth — Government & Corporate Bonds | Choice Wealth";
      description =
        "Access government and corporate bonds reviewed by yield, credit, duration, liquidity, tax impact and portfolio role with Choice Wealth.";
      keywords =
        "bonds, government bonds, corporate bonds, PSU bonds, G-Secs, SDLs, T-Bills, 54EC bonds, tax-free bonds, coupon vs YTM, Choice Wealth bonds";
    } else if (route.type === "sifProduct") {
      title = "Specialised Investment Funds (SIF) in India | SIF Review | Choice Wealth";
      description =
        "SIFs expand the way capital can be allocated — a new regulated category between mutual funds and PMS. Review strategy, risk, liquidity, minimum investment and portfolio fit with Choice Wealth.";
      keywords =
        "Specialised Investment Funds, SIF India, SIF investment, SIF vs mutual funds, SIF vs PMS, SIF review, Choice Wealth SIF";
    } else if (route.type === "ncdProduct") {
      title = "Non-Convertible Debentures (NCD) in India | NCD Review | Choice Wealth";
      description =
        "Seeking more from fixed income? Review Non-Convertible Debentures across issuer quality, credit rating, coupon, tenure, liquidity, taxation and portfolio role with Choice Wealth before allocation.";
      keywords =
        "Non-Convertible Debentures, NCD investment India, NCD review, secured NCD, NCD vs bonds, NCD taxation, issuer credit rating, Choice Wealth NCD";
    } else if (route.type === "npsProduct") {
      title = "National Pension System (NPS) in India | NPS Retirement Review | Choice Wealth";
      description =
        "A retirement plan that stays disciplined. Review the National Pension System with Choice Wealth across Tier I vs Tier II, Active vs Auto Choice, tax context, annuity and broader portfolio fit.";
      keywords =
        "National Pension System, NPS India, NPS investment, NPS retirement review, NPS tax benefit, Tier I vs Tier II, Active vs Auto Choice, Choice Wealth NPS";
    } else if (route.type === "blogEdit") {
      title = "Edit Blog | Choice Wealth Editorial System";
      description = "Edit an existing blog draft or published article.";
      keywords = "edit blog, editorial route, Choice Wealth";
    } else if (route.type === "blogArticle") {
      const blog = seoBlog;
      if (blog) {
        title = blog.seoTitle || `${blog.title} | Choice Wealth`;
        description = blog.seoDescription || blog.excerpt;
        keywords = `${blog.category}, ${blog.authorName}, Choice Wealth blog, PMS insights`;
        ogType = "article";
        articleSchema = {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: blog.title,
          description,
          author: {
            "@type": "Organization",
            name: blog.authorName,
          },
          publisher: {
            "@type": "Organization",
            name: "Choice Wealth",
          },
          datePublished: blog.publishedAt,
          dateModified: blog.publishedAt,
          mainEntityOfPage: canonicalUrl,
        };
      } else {
        title = "Blog Not Found | Choice Wealth";
        description = "The requested article route does not exist.";
        keywords = "blog not found, Choice Wealth";
      }
    } else if (route.type === "notFound") {
      title = "404 | Choice Wealth";
      description = "The requested page does not exist.";
      keywords = "404, Choice Wealth";
    }

    const shortTitle = SHORT_TITLES[route.type];
    if (shortTitle) {
      title = shortTitle;
    }

    // Open Graph / Twitter default to the page title/description; canonical defaults
    // to the live URL. These are then overridden by the Excel-sourced SEO metadata
    // (src/data/seoMetadata.ts) for any route it covers — see the block below.
    let ogTitle = title;
    let ogDescription = description;
    let twitterTitle = title;
    let twitterDescription = description;
    let canonicalHref = canonicalUrl;

    // Per-page SEO metadata from the Choice Wealth keyword/meta spreadsheet. Applied
    // per field: a field overrides the default only when the sheet populated it; a
    // blank cell leaves the existing page value unchanged (values are never invented).
    const seo = SEO_METADATA[route.type];
    if (seo) {
      if (seo.metaTitle) {
        title = seo.metaTitle;
      }
      if (seo.metaDescription) {
        description = seo.metaDescription;
      }
      // OG/Twitter fall back to the (possibly just-overridden) title/description.
      ogTitle = seo.ogTitle ?? title;
      ogDescription = seo.ogDescription ?? description;
      twitterTitle = seo.twitterTitle ?? title;
      twitterDescription = seo.twitterDescription ?? description;
      if (seo.canonical) {
        // Keep the exact path from the sheet, but follow the host that served the page.
        canonicalHref = rebaseToCurrentOrigin(seo.canonical);
      }
    }

    document.title = title;
    ensureMeta("description", description);
    ensureMeta("keywords", keywords);
    ensureMeta("og:title", ogTitle, true);
    ensureMeta("og:description", ogDescription, true);
    ensureMeta("og:type", ogType, true);
    ensureMeta("og:url", canonicalHref, true);
    ensureMeta("twitter:card", "summary_large_image");
    ensureMeta("twitter:title", twitterTitle);
    ensureMeta("twitter:description", twitterDescription);
    ensureLink("canonical", canonicalHref);
    ensureJsonLd("blog-article", articleSchema);

    // Structured data from the Choice Wealth schema sheet (src/data/pageSchema/).
    // Each sheet row is a complete @graph that already carries its own Organization /
    // WebSite nodes, so a page with a row emits that row alone. Pages with no row
    // (home, blog, /private-client-wealth, 404, v2) fall back to the identity graph.
    // Emitting both would put two nodes sharing an @id but holding different values
    // into one document.
    // Loaded lazily so each route pulls only its own graph, not all 35.
    let cancelled = false;
    void loadPageSchema(route.type).then((pageSchema) => {
      if (cancelled) {
        return;
      }
      ensureJsonLd("cw-identity", pageSchema ? null : CW_IDENTITY_SCHEMA);
      ensureJsonLd("cw-page-schema", pageSchema);
    });

    return () => {
      cancelled = true;
    };
  }, [pathname, route, seoBlog]);

  let page = <NotFoundPage />;
  if (route.type === "v2") {
    page = <V2PageLazy route={route.route} />;
  } else if (route.type === "v2Index") {
    page = <V2IndexPageLazy />;
  } else if (route.type === "modernHomeThree") {
    page = <ModernHomePageThree />;
  } else if (route.type === "blogIndex") {
    page = <BlogIndexPage />;
  } else if (route.type === "blogManage") {
    page = isAuthenticated ? <BlogManagePage /> : <AdminLoginPage />;
  } else if (route.type === "adminLogin") {
    page = <AdminLoginPage />;
  } else if (route.type === "blogCreate") {
    page = isAuthenticated ? <BlogCreatePage /> : <AdminLoginPage />;
  } else if (route.type === "blogPreview") {
    const previewBlog = readPreviewBlog();
    page = previewBlog ? <BlogArticlePage previewBlog={previewBlog} isPreview /> : <NotFoundPage />;
  } else if (route.type === "setupCall") {
    page = <SetupCallPage />;
  } else if (route.type === "setupCallMinimal") {
    page = <SetupCallMinimalPage />;
  } else if (route.type === "productsHub") {
    page = <ProductsHubPage />;
  } else if (route.type === "privacyPolicy") {
    page = <PrivacyPolicyPage />;
  } else if (route.type === "termsAndConditions") {
    page = <TermsAndConditionsPage />;
  } else if (route.type === "boardMembersPageTwo") {
    page = <ChoiceGroupPage />;
  } else if (route.type === "aboutUs") {
    page = <AboutUsPage />;
  } else if (route.type === "ourPhilosophy") {
    page = <OurPhilosophyPage />;
  } else if (route.type === "coreTeam") {
    page = <CoreTeamPage />;
  } else if (route.type === "board") {
    page = <BoardPage />;
  } else if (route.type === "wealthManagers") {
    page = <WealthManagersPage />;
  } else if (route.type === "contactUs") {
    page = <ContactUsPage />;
  } else if (route.type === "servicesHub") {
    page = <ServicesHubPage />;
  } else if (route.type === "portfolioHealthReview") {
    page = <PortfolioHealthReviewPage />;
  } else if (route.type === "retirementWealthReview") {
    page = <RetirementWealthReviewPage />;
  } else if (route.type === "structuredWealthReview") {
    page = <StructuredWealthReviewPage />;
  } else if (route.type === "globalInvestmentSolutions") {
    page = <GlobalInvestmentSolutionsPage />;
  } else if (route.type === "taxAwarePortfolioReview") {
    page = <TaxAwarePortfolioReviewPage />;
  } else if (route.type === "legacyWealthTransferCoordination") {
    page = <LegacyWealthTransferCoordinationPage />;
  } else if (route.type === "whoWeServe") {
    page = <WhoWeServeHubPage />;
  } else if (route.type === "whoWeServeHniPrivateWealth") {
    page = <HniPrivateWealthManagementPage />;
  } else if (route.type === "whoWeServeNriGlobalIndianWealth") {
    page = <NriGlobalIndianWealthManagementPage />;
  } else if (route.type === "whoWeServeCorporateTreasury") {
    page = <CorporateTreasurySolutionsPage />;
  } else if (route.type === "whoWeServeFamilyOffice") {
    page = <FamilyOfficeBusinessFamiliesWhoWeServePage />;
  } else if (route.type === "whoWeServeBusinessOwnersPromoters") {
    page = <BusinessOwnersPromotersWhoWeServePage />;
  } else if (route.type === "whoWeServeFoundersEntrepreneurs") {
    page = <FoundersEntrepreneursWhoWeServePage />;
  } else if (route.type === "whoWeServeCxosSeniorProfessionals") {
    page = <CxosSeniorProfessionalsWhoWeServePage />;
  } else if (route.type === "whoWeServePrivateClientWealth") {
    page = <PrivateClientWealthWhoWeServePage />;
  } else if (route.type === "portfolioManagementServicesTwo") {
    page = <PortfolioManagementServicesTwoPage />;
  } else if (route.type === "corporateFdTwo") {
    page = <CorporateFixedDepositsTwoPage />;
  } else if (route.type === "fixedIncomeProduct") {
    page = <FixedIncomeProductPage />;
  } else if (route.type === "mldProduct") {
    page = <MarketLinkedDebenturesPage />;
  } else if (route.type === "mutualFundProduct") {
    page = <MutualFundPage />;
  } else if (route.type === "aifProduct") {
    page = <AlternativeInvestmentFundsPage />;
  } else if (route.type === "sifProduct") {
    page = <SpecialisedInvestmentFundsPage />;
  } else if (route.type === "ncdProduct") {
    page = <NonConvertibleDebenturesPage />;
  } else if (route.type === "npsProduct") {
    page = <NationalPensionSystemPage />;
  } else if (route.type === "bondsProduct") {
    page = <BondsInvestmentServicesPage />;
  } else if (route.type === "blogEdit") {
    page = isAuthenticated ? <BlogCreatePage slug={route.slug} /> : <AdminLoginPage />;
  } else if (route.type === "blogArticle") {
    page = <BlogArticlePage slug={route.slug} />;
  }

  // Keep the loader over everything until the new route's content has mounted and
  // settled. `readyPath !== pathname` means the current page isn't on screen yet.
  const routeLoading = readyPath !== pathname;

  return (
    <>
      <AppLayout
        hideGlobalChrome={
          route.type === "notFound" || route.type === "v2" || route.type === "v2Index"
        }
        showGlobalHeader={route.type === "v2" || route.type === "v2Index"}
      >
        {route.type === "modernHomeThree" ? (
          // Hero renders eagerly + instantly (LCP); the rest of the page streams in beneath
          // it with a transparent fallback so the hero is never covered by a loader.
          <>
            <HomeHero />
            {homeBodyReady ? (
              <Suspense fallback={null}>
                {page}
                <PageReadySignal routeKey={pathname} onReady={handleRouteReady} />
              </Suspense>
            ) : null}
          </>
        ) : (
          // fallback={null}: the covering loader is the top-level one below (outside
          // AppLayout's transform). A fallback here would render INSIDE the route-enter
          // transform and leak the light page + faint logo on refresh. `routeLoading`
          // stays true until PageReadySignal fires, so the top-level loader covers this.
          <Suspense fallback={null}>
            {page}
            <PageReadySignal routeKey={pathname} onReady={handleRouteReady} />
          </Suspense>
        )}
      </AppLayout>

      {/* The loader is rendered OUTSIDE AppLayout on purpose. AppLayout wraps its
          children in a `route-enter` transform animation (translateY), and a CSS
          transform on an ancestor turns `position: fixed` into "fixed relative to
          that ancestor" — which shifts/uncovers the loader and lets the light page
          background (with the faint light-variant logo) leak through. Kept here at
          the top level, the loader is truly viewport-fixed and always fully covers.
          Option B: covers EVERY route on navigation, including home. On a cold load
          readyPath starts equal to pathname, so it only appears on in-app navigation
          and never covers the initial home hero (index.html #app-boot-loader owns
          first paint). */}
      {routeLoading ? <PageContentFallback /> : null}
    </>
  );
}

export default App;
